import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import { EBlackbaudRaisersEdgeUnsubscribeSyncActions } from '@feathr/blackbox';
import type { IIntegrationUISettingsSection, TIntegrationUISettingValue } from '@feathr/components';
import { IntegrationSettingsPanel } from '@feathr/components';

import { SOLICIT_CODE_KEY, UNSUBSCRIBE_ACTION_KEY } from '../../raisersEdgeConfig';
import { useSolicitCodeOptions } from './RaisersEdgeUnsubscribeSettings.hooks';
import {
  DEFAULT_UNSUBSCRIBE_PREFERENCE,
  getUnsubscribeValues,
  updateUnsubscribePreference,
} from './RaisersEdgeUnsubscribeSettings.utils';

interface IProps {
  integration: BlackbaudRaisersEdgeIntegration;
  /** The config's settings sections. */
  sections: readonly IIntegrationUISettingsSection[];
  /** Disables every setting with this reason, e.g. while the page saves. */
  disabledReason?: string;
}

function RaisersEdgeUnsubscribeSettings({
  disabledReason,
  integration,
  sections,
}: Readonly<IProps>): JSX.Element {
  const preference =
    integration.get('unsubscribe_sync_preference') ?? DEFAULT_UNSUBSCRIBE_PREFERENCE;
  const values = getUnsubscribeValues(preference);
  const solicitCodeOptions = useSolicitCodeOptions(
    integration,
    values[UNSUBSCRIBE_ACTION_KEY] === EBlackbaudRaisersEdgeUnsubscribeSyncActions.SolicitCode,
  );

  // Stage the edit on the model; the page's Save & Sync persists it.
  function handleChange(key: string, value: TIntegrationUISettingValue): void {
    integration.set({
      unsubscribe_sync_preference: updateUnsubscribePreference(preference, key, value),
    });
  }

  return (
    <IntegrationSettingsPanel
      disabledReason={disabledReason}
      name={'raisers-edge-settings'}
      onChange={handleChange}
      sections={sections}
      selectOptions={solicitCodeOptions ? { [SOLICIT_CODE_KEY]: solicitCodeOptions } : undefined}
      values={values}
    />
  );
}

export default observer(RaisersEdgeUnsubscribeSettings);
